import React from "react";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchUserSubmissions, deletePaperSubmission, getUserProfile, getActiveConferences } from "../services/api"; // Import delete function
import PaperSubmission from "../components/Paper/PaperSubmission";
import RegistrationPaymentModal from "../components/Payments/RegistrationPaymentModal.jsx";
import AttendeeRegistrationModal from "../components/Payments/AttendeeRegistrationModal.jsx";
import PaperEditModal from "../components/Admin/PaperEditModal.jsx";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [activeTab, setActiveTab] = useState("submit"); // New state for tab management
  const [deleteLoading, setDeleteLoading] = useState(null); // Track which submission is being deleted
  const [pendingRegistration, setPendingRegistration] = useState(null); // Paper selected for registration
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [conferences, setConferences] = useState([]);
  const [conferencesLoading, setConferencesLoading] = useState(false);
  const [selectedConferenceForAttendee, setSelectedConferenceForAttendee] = useState(null);
  const [showAttendeeRegistrationModal, setShowAttendeeRegistrationModal] = useState(false);
  const LOCALE = 'en-GB';
  const actionColumnStyle = { minWidth: '100px', whiteSpace: 'nowrap' };

  const mountedRef = useRef(true);
  useEffect(()=>{ return () => { mountedRef.current = false; }; }, []);

  const rememberAndRedirectToLogin = () => {
    const targetPath = `${location.pathname}${location.search}${location.hash}`;
    localStorage.setItem('postLoginRedirect', targetPath);
    navigate('/login', { state: { from: location }, replace: true });
  };

  // Safe state helpers
  const safeSet = (setter) => (...args) => { if (mountedRef.current) setter(...args); };
  const safeSetLoading = safeSet(setLoading);
  const safeSetSubmissions = safeSet(setSubmissions);
  const safeSetError = safeSet(setError);
  const safeSetSuccessMessage = safeSet(setSuccessMessage);
  const safeSetCurrentUserEmail = safeSet(setCurrentUserEmail);

  // Function to load user submissions
  const loadUserSubmissions = async () => {
    safeSetLoading(true);
    try {
      const data = await fetchUserSubmissions(); // Use the user-specific function
      safeSetSubmissions(data);
      safeSetError("");
    } catch (error) {
      console.error("Error fetching user submissions:", error);
      if (error?.response?.status === 403) {
        console.log("403 Forbidden error detected, redirecting to login");
        rememberAndRedirectToLogin();
        return;
      }
      safeSetError("Failed to load your submissions");
    } finally {
      safeSetLoading(false);
    }
  };

  // Handle URL parameters to set active tab
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get("tab");
    const submitted = searchParams.get("submitted");

    if (tab && ["submit", "submissions"].includes(tab)) {
      setActiveTab(tab);

      // If redirected to submissions tab, refresh the submissions list
      if (tab === "submissions") {
        loadUserSubmissions();

        // Show success message if redirected after submission
        if (submitted === "true") {
          safeSetSuccessMessage(
            "🎉 Paper submitted successfully! Your submission appears below."
          );
          // Clear success message after 5 seconds
          setTimeout(() => safeSetSuccessMessage(""), 5000);
          // Clean up URL parameters
          navigate("/dashboard?tab=submissions", { replace: true });
        }
      }
    }
  }, [location.search, navigate]);

  useEffect(() => {
    loadUserSubmissions();
    // Fetch current user profile for email matching
    (async () => {
      try {
        const profile = await getUserProfile();
        const email = profile?.email || "";
        safeSetCurrentUserEmail(email.toLowerCase());
      } catch (err) {
        console.error("Error loading user profile:", err);
      }
    })();
  }, [navigate]);

  // Load conferences when attendee registration tab is opened
  const loadConferencesData = async () => {
    setConferencesLoading(true);
    try {
      const conferenceList = await getActiveConferences();
      if (mountedRef.current) setConferences(conferenceList);
    } catch (error) {
      console.error("Error loading conferences:", error);
      if (mountedRef.current) setConferences([]);
    } finally {
      if (mountedRef.current) setConferencesLoading(false);
    }
  };

  const handleAttendeeRegistration = (conference) => {
    setSelectedConferenceForAttendee(conference);
    setShowAttendeeRegistrationModal(true);
  };

  const getRegistrationWindowStatus = (conference) => {
    if (!conference.registrationEnabled) return { isOpen: false, message: 'Registration disabled' };
    if (conference.registrationDeadline && new Date(conference.registrationDeadline) < new Date()) {
      return { isOpen: false, message: 'Deadline passed' };
    }
    return { isOpen: true, message: '' };
  };

  const hasSubmissionInOpenOrActiveConference = submissions.some((submission) => {
    const conferenceStatus = submission?.conferenceId?.status?.toLowerCase?.();
    return conferenceStatus === 'open' || conferenceStatus === 'active';
  });

  const canShowAttendeeRegistrationTab = !hasSubmissionInOpenOrActiveConference;

  useEffect(() => {
    if (!canShowAttendeeRegistrationTab && activeTab === 'register') {
      setActiveTab('submit');
    }
  }, [canShowAttendeeRegistrationTab, activeTab]);

  const formatDateMethod = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Etc/GMT' };
    return new Date(dateString).toLocaleDateString(LOCALE, options);
  };

  const openEditModal = (submission) => {
    if (!canEditSubmission(submission)) return;
    setSelectedSubmission(submission);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedSubmission(null);
  };

  const handleSubmissionUpdated = async () => {
    await loadUserSubmissions();
    closeEditModal();
  };

  // Only submitter (email match) can edit while the conference is Open
  const canEditSubmission = (submission) => {
    const status = (submission?.conferenceId?.status || "").toLowerCase();
    return (
      status === "open" &&
      currentUserEmail &&
      submission?.submittedBy?.email &&
      submission.submittedBy.email.toLowerCase() === currentUserEmail
    );
  };

  /**
   * Handle deletion of a paper submission
   * @param {string} submissionId - The ID of the submission to delete
   */
  const handleDeleteSubmission = async (submissionId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this submission? This action cannot be undone."
      )
    ) {
      return;
    }

    setDeleteLoading(submissionId);
    try {
      await deletePaperSubmission(submissionId);
      // Remove the deleted submission from the local state
      setSubmissions(submissions.filter((sub) => sub._id !== submissionId));
      setError(""); // Clear any previous errors
    } catch (error) {
      console.error("Error deleting submission:", error);

      // Check if the error is 403 (Forbidden) and redirect to login
      if (error.response && error.response.status === 403) {
        console.log(
          "403 Forbidden error detected during deletion, redirecting to login"
        );
        rememberAndRedirectToLogin();
        return;
      }

      setError("Failed to delete submission. Please try again.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const submissionGroups = [
    {
      title: "Current Conferences",
      submissions: submissions.filter(
        (submission) =>
          submission.conferenceId?.status?.toLowerCase() !== "ended"
      ),
      isPast: false,
    },
    {
      title: "Past Conferences",
      submissions: submissions.filter(
        (submission) =>
          submission.conferenceId?.status?.toLowerCase() === "ended"
      ),
      isPast: true,
    },
  ];

  if (loading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {pendingRegistration && (
        <RegistrationPaymentModal
          paper={pendingRegistration}
          onClose={() => setPendingRegistration(null)}
          onSuccess={() => {
            // Reflect new registration in aggregated stats
            setSubmissions(submissions.map((s) => {
              if (s._id !== pendingRegistration._id) return s;
              const prev = s.registrationStats || {};
              return {
                ...s,
                registrationStats: {
                  ...prev,
                  totalPaid: (prev.totalPaid || 0) + 1,
                  userHasRegistration: true,
                  userRegistrationCount: (prev.userRegistrationCount || 0) + 1,
                  anyAuthorHasRegistration: true,
                  otherAuthorsHaveRegistration: (prev.totalPaid || 0) + 1 > 1 ? ( (prev.userRegistrationCount || 0) < ((prev.totalPaid || 0) + 1) ) : false
                }
              };
            }));
            setPendingRegistration(null);
          }}
        />
      )}
      <div className="row">
        <div className="col-12">
          <h1 className="mb-4">User Dashboard</h1>

          {/* Tab Navigation */}
          <ul className="nav nav-tabs mb-4" id="dashboardTabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "submit" ? "active" : ""}`}
                onClick={() => setActiveTab("submit")}
                type="button"
                role="tab"
              >
                <i className="bi bi-plus-circle me-2"></i>
                Submit Manuscript
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${
                  activeTab === "submissions" ? "active" : ""
                }`}
                onClick={() => setActiveTab("submissions")}
                type="button"
                role="tab"
              >
                <i className="bi bi-file-earmark-text me-2"></i>
                My Submissions
              </button>
            </li>
            {canShowAttendeeRegistrationTab && (
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${
                    activeTab === "register" ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("register");
                    if (conferences.length === 0) {
                      loadConferencesData();
                    }
                  }}
                  type="button"
                  role="tab"
                >
                  <i className="bi bi-person-check me-2"></i>
                  Register as NON-presenting participant
                </button>
              </li>
            )}
          </ul>

          {/* Tab Content */}
          <div className="tab-content" id="dashboardTabsContent">
            {/* Submissions Tab */}
            {activeTab === "submissions" && (
              <div className="card">
                <div className="card-header">
                  <h2 className="mb-0">Submitted Manuscripts</h2>
                </div>
                <div className="card-body">
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  {successMessage && (
                    <div
                      className="alert alert-success alert-dismissible fade show"
                      role="alert"
                    >
                      {successMessage}
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setSuccessMessage("")}
                      ></button>
                    </div>
                  )}

                  {submissions.length === 0 ? (
                    <div className="text-center py-4">
                      <i
                        className="bi bi-file-earmark-text"
                        style={{ fontSize: "3rem", color: "#6c757d" }}
                      ></i>
                      <p className="text-muted mt-2">No submissions found.</p>
                      <button
                        className="btn btn-primary"
                        onClick={() => setActiveTab("submit")}
                      >
                        Submit Your First Paper
                      </button>
                    </div>
                  ) : (
                    <>
                      {submissionGroups.map((group) => group.submissions.length > 0 && (
                        <section key={group.title} className={group.isPast ? "mt-5" : ""}>
                          <h3 className={`border-bottom pb-2 mb-3${group.isPast ? " text-muted" : ""}`}>
                            {group.title}
                          </h3>
                          <div className="table-responsive">
                      <table className={`table table-hover${group.isPast ? " table-secondary" : ""}`}>
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Conference</th>
                            <th>Status</th>
                            <th>Registration</th>
                            <th>Participants</th>
                            <th>Submitted Date</th>
                            <th style={actionColumnStyle}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.submissions.map((submission) => (
                            <tr
                              key={submission.id || submission._id}
                              className={group.isPast ? "text-muted" : ""}
                            >
                              <td>
                                <strong>
                                  {submission.manuscriptTitle ||
                                    submission.title}
                                </strong>{" "}
                                <small className="text-muted">
                                  ({submission.manuscriptType})
                                </small>
                                <br />
                                <small className="text-muted">
                                  <strong>Authors:</strong>{" "}
                                  {submission.authors &&
                                  submission.authors.length > 0
                                    ? submission.authors.map(
                                        (author, index) => (
                                          <span key={index}>
                                            {author.firstName} {author.lastName}
                                            {author.isCorresponding && (
                                              <span className="text-primary">
                                                {" "}
                                                ✉
                                              </span>
                                            )}
                                            {index <
                                              submission.authors.length - 1 &&
                                              ", "}
                                          </span>
                                        )
                                      )
                                    : "No authors listed"}
                                </small>
                              </td>
                              <td>{submission.conferenceId?.title || "N/A"}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    submission.status === "accepted"
                                      ? group.isPast
                                        ? "bg-secondary"
                                        : "bg-success"
                                      : submission.status === "rejected"
                                      ? "bg-danger"
                                      : submission.status === "under_review"
                                      ? "bg-warning"
                                      : "bg-secondary"
                                  }`}
                                >
                                  {submission.status
                                    ? submission.status
                                        .replace(/_/g, " ")
                                        .replace(/\b\w/g, (l) =>
                                          l.toUpperCase()
                                        )
                                    : "Pending"}
                                </span>
                              </td>
                              <td>
                                {submission.status === "accepted" ? (
                                  submission.registrationStats
                                    ?.userHasRegistration ? (
                                    <span className="badge bg-success">
                                      You Registered
                                    </span>
                                  ) : (
                                    <span className="badge bg-warning text-dark">
                                      Not Registered
                                    </span>
                                  )
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {submission.status === "accepted" ? (
                                  <>
                                    <span className="badge bg-info text-dark" title={`Total confirmed registrations${submission.registrationStats?.userRegistrationCount ? ' (you are registered)' : ''}`}>
                                      {submission.registrationStats?.totalPaid || 0}
                                    </span>
                                    {submission.registrationStats?.userRegistrationCount > 0 && (
                                      <span className="badge bg-success ms-1" title="You have already registered for this paper / conference.">
                                        You Registered
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {submission.submissionDate
                                  ? new Date(
                                      submission.submissionDate
                                    ).toLocaleDateString(LOCALE, { timeZone: 'Etc/GMT' })
                                  : "N/A"}
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm" style={actionColumnStyle}>
                                  {group.isPast ? (
                                    <button
                                      className="btn btn-outline-secondary"
                                      disabled
                                      title="This conference has ended."
                                    >
                                      <i className="bi bi-archive"></i>
                                      <span className="ms-1">Past</span>
                                    </button>
                                  ) : (
                                    <>
                                  {canEditSubmission(submission) && (
                                    <button
                                      className="btn btn-outline-primary"
                                      title="Edit submission"
                                      onClick={() => openEditModal(submission)}
                                    >
                                      <i className="bi bi-pencil"></i>
                                      <span className="ms-1">Edit</span>
                                    </button>
                                  )}
                                  {submission.status === "under_review" &&
                                    submission.userRoles.includes(
                                      "submitter"
                                    ) && (
                                      <button
                                        className="btn btn-outline-danger"
                                        title="Delete"
                                        onClick={() =>
                                          handleDeleteSubmission(submission._id)
                                        }
                                        disabled={
                                          deleteLoading === submission._id
                                        }
                                      >
                                        {deleteLoading === submission._id ? (
                                          <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                          ></span>
                                        ) : (
                                          <>
                                            <i className="bi bi-trash"></i>
                                            <span className="ms-1">Delete</span>
                                          </>
                                        )}
                                      </button>
                                    )}
                                  {(() => {                                    
                                    const isAccepted = submission.status === 'accepted';
                                    const hasRole = submission.userRoles?.some(r => ['submitter','corresponding_author','co_author'].includes(r));
                                    const regEnabled = submission.conferenceId?.registrationEnabled === true;
                                    const userCount = submission.registrationStats?.userRegistrationCount || 0;
                                    const show = isAccepted && hasRole && regEnabled && userCount === 0;
                                    if (!show && isAccepted && hasRole) {
                                      // Instrument why button is hidden
                                      console.debug('[RegisterButton][Hidden]', {
                                        paperId: submission._id,
                                        isAccepted, hasRole, regEnabled, userCount,
                                        registrationStats: submission.registrationStats,
                                      });
                                      // If only blocker is registration disabled, show closed indicator
                                      if (!regEnabled && userCount === 0) {
                                        return (
                                          <>
                                            <button
                                              className="btn btn-outline-secondary"
                                              title="Registration is currently closed for this conference."
                                              disabled
                                            >
                                              <i className="bi bi-lock"></i>
                                              <span className="ms-1">Closed</span>
                                            </button>
                                            <span className="badge bg-secondary ms-1" title="Conference registration is disabled.">Closed</span>
                                          </>
                                        );
                                      }
                                    }
                                    return show ? (
                                      <>
                                        <button
                                          className="btn btn-outline-primary"
                                          title="Register (Create Your Registration)"
                                          onClick={() => setPendingRegistration(submission)}
                                        >
                                          <i className="bi bi-credit-card"></i>
                                          <span className="ms-1">Register</span>
                                        </button>
                                        {/* {submission.registrationStats?.otherAuthorsHaveRegistration && (
                                          <span className="badge bg-warning text-dark ms-1" title="Another author has already registered. You still need to register individually.">
                                            Others Registered
                                          </span>
                                        )} */}
                                      </>
                                    ) : null;
                                  })()}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                        </section>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Paper Submission Tab */}
            {activeTab === "submit" && (
              <div className="card">
                <div className="card-header">
                  <h2 className="mb-0">Submit a Manuscript</h2>
                </div>
                <div className="card-body">
                  <PaperSubmission />
                </div>
              </div>
            )}

            {/* Attendee Registration Tab */}
            {activeTab === "register" && canShowAttendeeRegistrationTab && (
              <div className="card">
                <div className="card-header">
                  <h2 className="mb-0">Register as Conference Attendee</h2>
                </div>
                <div className="card-body">
                  <div className="alert alert-info" role="alert">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Note:</strong> This feature is for participants without a paper submission. If you have submitted a paper that has been accepted, please use the <strong>Register</strong> button next to your paper in the <strong>My Submissions</strong> tab instead.
                  </div>
                  {conferencesLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading conferences...</span>
                      </div>
                      <p className="mt-2 text-muted">Loading conferences...</p>
                    </div>
                  ) : conferences.filter(conf => conf.status?.toLowerCase() !== 'ended').length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-calendar-event display-1 text-muted"></i>
                      <h5 className="mt-3 text-muted">No Active Conferences</h5>
                      <p className="text-muted">
                        {conferences.length > 0 
                          ? "All conferences have ended. Please check back later for new conferences."
                          : "There are currently no conferences available for registration."
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Conference</th>
                            <th>Location</th>
                            <th>Dates</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {conferences
                            .filter(conf => conf.status?.toLowerCase() !== 'ended')
                            .map((conference) => {
                            const regStatus = getRegistrationWindowStatus(conference);
                            return (
                              <tr key={conference._id}>
                                <td>
                                  <div className="fw-bold">{conference.title}</div>
                                  {conference.description && (
                                    <small className="text-muted">
                                      {conference.description.substring(0, 100)}...
                                    </small>
                                  )}
                                </td>
                                <td>{conference.location || 'N/A'}</td>
                                <td>
                                  <small>
                                    {formatDateMethod(conference.startDate)} - {formatDateMethod(conference.endDate)}
                                  </small>
                                </td>
                                <td>
                                  <span
                                    className={`badge ${
                                      conference.status === 'Open'
                                        ? 'bg-success'
                                        : conference.status === 'Active'
                                        ? 'bg-info'
                                        : conference.status === 'Close'
                                        ? 'bg-warning'
                                        : 'bg-secondary'
                                    }`}
                                  >
                                    {conference.status}
                                  </span>
                                </td>
                                <td>
                                  {regStatus.isOpen ? (
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => handleAttendeeRegistration(conference)}
                                    >
                                      <i className="bi bi-person-check me-1"></i>
                                      Register
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      disabled
                                      title={regStatus.message}
                                    >
                                      <i className="bi bi-lock me-1"></i>
                                      {regStatus.message}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <PaperEditModal
        paper={selectedSubmission}
        show={showEditModal}
        onHide={closeEditModal}
        onPaperUpdated={handleSubmissionUpdated}
        isUserContext={true}
      />

      {selectedConferenceForAttendee && (
        <AttendeeRegistrationModal
          conference={selectedConferenceForAttendee}
          onClose={() => {
            setShowAttendeeRegistrationModal(false);
            setSelectedConferenceForAttendee(null);
          }}
          onSuccess={() => {
            setShowAttendeeRegistrationModal(false);
            setSelectedConferenceForAttendee(null);
          }}
          show={showAttendeeRegistrationModal}
        />
      )}
    </div>
  );
};

export default Dashboard;
